<template>
  <UIModal
    v-model="isOpen"
    :close-button="false"
    :close-on-click-outside="false"
    disable-content-overflow-hidden
    disable-content-overflow-auto
  >
    <!-- Header -->
    <template #header>
      <h2 class="halloween-expedition-modal__title">{{ $t('HalloweenWitchWelcomeModalTitle') }}</h2>
      <div class="halloween-expedition-modal__text">
        <p>{{ $t('HalloweenWitchWelcomeModalSubtitle') }}</p>
      </div>
    </template>

    <!-- Content -->
    <div class="halloween-expedition-modal__content">
      <div class="halloween-expedition-modal__bg-img">
        <img src="/img/halloween/bg/bg_witch_welcome_modal.png" alt="nature" />
      </div>
      <div ref="witchContainer" class="halloween-expedition-modal__witch-img"></div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="halloween-expedition-modal__footer">
        <HalloweenGenericButton @click="continueAction">
          {{ $t('coreContinue') }}
        </HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { Application, Assets } from 'pixi.js';
import { Spine } from 'pixi-spine';

const isOpen = defineModel<boolean>({ default: false });
const emit = defineEmits(['closed']);
const witchContainer = ref<HTMLDivElement | null>(null);

let pixiApp: Application | null = null;

const initWitchAnimation = async () => {
  if (!witchContainer.value) {
    return;
  }

  pixiApp = new Application({
    width: 500,
    height: 600,
    backgroundColor: 0x000000,
    backgroundAlpha: 0,
    antialias: true,
    resolution: window?.devicePixelRatio || 1,
    autoDensity: true
  });

  witchContainer.value.appendChild(pixiApp.view as HTMLCanvasElement);

  try {
    const skelPath = '/assets/animations/halloween/Witch.skel';

    const assetData = await Assets.load(skelPath);

    const witchSpine = new Spine(assetData.spineData);

    const initialBounds = witchSpine.getBounds();

    const targetWidth = 480;
    const targetHeight = 580;
    const scaleX = targetWidth / initialBounds.width;
    const scaleY = targetHeight / initialBounds.height;
    const scale = Math.min(scaleX, scaleY);

    witchSpine.scale.set(scale);

    const scaledBounds = witchSpine.getBounds();
    witchSpine.x = pixiApp.screen.width / 2 - scaledBounds.x - scaledBounds.width / 2;
    witchSpine.y = pixiApp.screen.height / 2 - scaledBounds.y - scaledBounds.height / 2;

    witchSpine.state.setAnimation(0, 'idle', true);
    pixiApp.stage.addChild(witchSpine);
    pixiApp.ticker.add(() => {
      if (pixiApp) witchSpine.update(pixiApp.ticker.deltaTime / 60);
    });
  } catch (error) {
    console.error('Failed to load witch animation:', error);
  }
};

const destroyWitchAnimation = () => {
  setTimeout(() => {
    if (pixiApp) {
      pixiApp.destroy(true, { children: true, texture: true, baseTexture: true });
      pixiApp = null;
    }
  }, 200);
};

onMounted(() => {
  watch(isOpen, async (newValue) => {
    if (newValue) {
      await nextTick();
      await initWitchAnimation();
    } else {
      destroyWitchAnimation();
    }
  });
});

const continueAction = () => {
  isOpen.value = false;
  emit('closed');
};

onBeforeUnmount(() => {
  destroyWitchAnimation();
});
</script>

<style lang="scss" scoped>
.halloween-expedition-modal {
  &__title {
    font-family: Grenze, serif;
    font-weight: 700;
    font-size: 32px;
    color: #ffffff;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 8px;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  &__content {
    position: relative;

    @media screen and (max-width: 640px) {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  &__bg-img {
    img {
      display: block;
      width: 100%;
    }
  }

  &__witch-img {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 400px;
    height: 500px;

    @media (max-width: 900px) {
      width: 350px;
      height: 437px;
    }
    @media (max-width: 770px) {
      width: 300px;
      height: 375px;
    }

    :deep(canvas) {
      width: 100% !important;
      height: 100% !important;
    }
  }

  &__text {
    margin-bottom: 8px;
    text-align: center;
    color: #90989d;
  }

  &__footer {
    display: flex;
    justify-content: center;
    margin-top: 65px;
  }
}
</style>
